import { createBrowserRouter, type RouteObject } from "react-router"

import { AuthLayout } from "@/app/layouts/auth-layout"
import { NotFoundPage, RouteErrorPage } from "@/app/error-pages"
import { PublicOnly, RequireCoordinator, RequireOwner } from "@/app/guards"

/** Lazy route helper: each feature is its own chunk (spec §5.3 route map). */
function page(load: () => Promise<{ default: React.ComponentType }>): Pick<RouteObject, "lazy"> {
  return { lazy: async () => ({ Component: (await load()).default }) }
}

export const router = createBrowserRouter([
  {
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <PublicOnly />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              { path: "/login", ...page(() => import("@/features/auth/routes").then((m) => ({ default: m.Login }))) },
              {
                path: "/forgot-password",
                ...page(() => import("@/features/auth/routes").then((m) => ({ default: m.Forgot }))),
              },
            ],
          },
        ],
      },
      {
        element: <AuthLayout />,
        children: [
          {
            path: "/reset-password",
            ...page(() => import("@/features/auth/routes").then((m) => ({ default: m.Reset }))),
          },
          {
            path: "/set-password",
            ...page(() => import("@/features/auth/routes").then((m) => ({ default: m.SetPassword }))),
          },
        ],
      },
      // Public contract-signing link (ADR-0018): no login, own minimal layout.
      { path: "/sign", ...page(() => import("@/features/signing/sign-page")) },
      {
        element: <RequireOwner />,
        children: [
          {
            lazy: async () => ({ Component: (await import("@/app/layouts/owner-layout")).OwnerLayout }),
            children: [
              { index: true, ...page(() => import("@/features/dashboard/dashboard-page")) },
              {
                element: <RequireOwner perm="bookings.view" />,
                children: [
                  { path: "/bookings", ...page(() => import("@/features/bookings/bookings-page")) },
                  { path: "/bookings/:id", ...page(() => import("@/features/bookings/booking-detail-page")) },
                ],
              },
              {
                element: <RequireOwner perm="bookings.create" />,
                children: [
                  { path: "/bookings/new", ...page(() => import("@/features/bookings/booking-form-page")) },
                  { path: "/bookings/:id/edit", ...page(() => import("@/features/bookings/booking-form-page")) },
                ],
              },
              {
                element: <RequireOwner perm="invoices.view" />,
                children: [{ path: "/invoices", ...page(() => import("@/features/invoices/invoices-page")) }],
              },
              {
                element: <RequireOwner perm="customers.view" />,
                children: [{ path: "/customers", ...page(() => import("@/features/customers/customers-page")) }],
              },
              {
                element: <RequireOwner perm="coordinators.view" />,
                children: [
                  { path: "/coordinators", ...page(() => import("@/features/coordinators/coordinators-page")) },
                  {
                    path: "/coordinators/:id",
                    ...page(() => import("@/features/coordinators/coordinator-detail-page")),
                  },
                ],
              },
              {
                element: <RequireOwner perm="partners.view" />,
                children: [
                  { path: "/partners", ...page(() => import("@/features/partners/partners-page")) },
                  { path: "/partners/:id", ...page(() => import("@/features/partners/partner-detail-page")) },
                ],
              },
              {
                element: <RequireOwner perm="expenses.view" />,
                children: [{ path: "/expenses", ...page(() => import("@/features/expenses/expenses-page")) }],
              },
              {
                element: <RequireOwner perm="reports.view" />,
                children: [{ path: "/reports", ...page(() => import("@/features/reports/reports-page")) }],
              },
              {
                element: <RequireOwner perm="lookups.manage" />,
                children: [{ path: "/lookups", ...page(() => import("@/features/lookups/lookups-page")) }],
              },
              { path: "/account", ...page(() => import("@/features/account/account-page")) },
              { path: "/guides", ...page(() => import("@/features/guides/guides-page")) },
              { path: "/guides/:slug", ...page(() => import("@/features/guides/guide-page")) },
              {
                element: <RequireOwner superOnly />,
                children: [
                  { path: "/admin/users", ...page(() => import("@/features/admin/users-page")) },
                  { path: "/settings", ...page(() => import("@/features/settings/settings-page")) },
                ],
              },
              { path: "*", element: <NotFoundPage /> },
            ],
          },
        ],
      },
      {
        element: <RequireCoordinator />,
        children: [
          {
            lazy: async () => ({ Component: (await import("@/app/layouts/portal-layout")).PortalLayout }),
            children: [
              { path: "/portal", ...page(() => import("@/features/portal/portal-home-page")) },
              { path: "/portal/bookings", ...page(() => import("@/features/portal/portal-my-bookings-page")) },
              { path: "/portal/bookings/new", ...page(() => import("@/features/portal/portal-booking-form-page")) },
              { path: "/portal/bookings/:id", ...page(() => import("@/features/portal/portal-booking-detail-page")) },
              {
                path: "/portal/bookings/:id/edit",
                ...page(() => import("@/features/portal/portal-booking-form-page")),
              },
              { path: "/portal/customers", ...page(() => import("@/features/portal/portal-customers-page")) },
              { path: "/portal/earnings", ...page(() => import("@/features/portal/portal-bookings-page")) },
              { path: "/portal/agreements", ...page(() => import("@/features/portal/portal-agreements-page")) },
              { path: "/portal/payouts", ...page(() => import("@/features/portal/portal-payouts-page")) },
              { path: "/portal/payouts/:id", ...page(() => import("@/features/portal/portal-receipt-page")) },
              { path: "/portal/notifications", ...page(() => import("@/features/portal/portal-notifications-page")) },
              { path: "/portal/account", ...page(() => import("@/features/account/account-page")) },
              { path: "/portal/guides", ...page(() => import("@/features/guides/guides-page")) },
              { path: "/portal/guides/:slug", ...page(() => import("@/features/guides/guide-page")) },
            ],
          },
        ],
      },
    ],
  },
])
